import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type AvatarSize = 'sm' | 'md' | 'lg';

const SIZES = {
  sm: { box: 'size-10', text: 'text-sm', icon: 'size-5' },
  md: { box: 'size-14', text: 'text-lg', icon: 'size-6' },
  lg: { box: 'size-24', text: 'text-3xl', icon: 'size-10' },
} as const satisfies Record<AvatarSize, { box: string; text: string; icon: string }>;

// Solid colours with white initials (Gmail / Slack style), all dark enough for readable white text.
// Full class names so Tailwind keeps them; the seed picks one, so a person always gets the same colour.
const COLORS = [
  'bg-blue-600',
  'bg-emerald-600',
  'bg-amber-600',
  'bg-rose-600',
  'bg-violet-600',
  'bg-teal-600',
  'bg-orange-600',
  'bg-indigo-600',
  'bg-pink-600',
  'bg-cyan-700',
] as const;

/** "Ana Marić" → "AM", "ana" → "A", "" → "" */
const initialsOf = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? [words[0], words.at(-1)!] : words;
  return letters.map((word) => [...word][0]).join('').toLocaleUpperCase();
};

const hash = (text: string) => [...text].reduce((h, char) => (h * 31 + char.codePointAt(0)!) >>> 0, 0);

/** Round avatar: the image if there is one, otherwise initials (people) or a photo placeholder (places). */
@Component({
  selector: 'app-avatar',
  templateUrl: './avatar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-flex shrink-0' },
})
export class AvatarComponent {
  readonly src = input<string | undefined>();
  /** Used for the alt text and the initials. */
  readonly name = input('');
  /** Stable value (e.g. the id) that picks the background colour. */
  readonly seed = input('');
  readonly size = input<AvatarSize>('md');
  readonly fallback = input<'initials' | 'photo'>('initials');

  protected readonly dimensions = computed(() => SIZES[this.size()]);
  protected readonly initials = computed(() => (this.fallback() === 'initials' ? initialsOf(this.name()) : ''));
  /** One class string: [class] arrays need one class per entry. */
  protected readonly initialsClass = computed(() => {
    const { box, text } = this.dimensions();
    return `${box} ${text} ${COLORS[hash(this.seed() || this.name()) % COLORS.length]}`;
  });
}
